import { useEffect, useRef, useState } from 'react'

interface Props {
  values: number[]
  totalQuarters: number
  color?: string
  height?: number
}

/**
 * Mini trend chart with the X axis spanning the full game (1..totalQuarters).
 * As quarters advance, the line grows from the left edge toward the right.
 *
 * The SVG renders at the container's actual pixel width (measured via
 * ResizeObserver) instead of stretching a fixed viewBox — that avoids the
 * stroke-width artifacts that occur when anisotropic viewport scaling
 * interacts with vector-effect="non-scaling-stroke".
 */
export default function Sparkline({
  values,
  totalQuarters,
  color = '#94a3b8',
  height = 28,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width)
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const steps = Math.max(1, totalQuarters - 1)

  let polyline: React.ReactNode = null
  let dotLeft = 0
  let dotTop = 0
  let hasDot = false

  if (values.length > 0 && width > 0) {
    const min = Math.min(...values)
    const max = Math.max(...values)
    const range = max - min || 1
    // Pad vertically so the 1.5px stroke is fully inside the SVG even when
    // a point sits at the data min/max — otherwise the bottom (or top) half
    // of the stroke is clipped by the viewport, making horizontal segments
    // along an edge look thinner than diagonal ones.
    const yPad = 1.5
    const usableH = Math.max(1, height - 2 * yPad)
    const y = (v: number) => yPad + (1 - (v - min) / range) * usableH
    const x = (i: number) => (i / steps) * width

    const lastIndex = values.length - 1
    dotLeft = x(lastIndex)
    dotTop = y(values[lastIndex])
    hasDot = true

    if (values.length >= 2) {
      const points = values
        .map((v, i) => `${x(i).toFixed(2)},${y(v).toFixed(2)}`)
        .join(' ')
      polyline = (
        <svg
          width={width}
          height={height}
          shapeRendering="geometricPrecision"
          aria-hidden
        >
          <polyline
            points={points}
            fill="none"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    }
  }

  return (
    <div ref={containerRef} className="relative" style={{ height }}>
      {polyline}
      {hasDot && (
        <div
          className="absolute rounded-full"
          style={{
            width: 5,
            height: 5,
            left: dotLeft - 2.5,
            top: dotTop - 2.5,
            backgroundColor: color,
          }}
          aria-hidden
        />
      )}
    </div>
  )
}
